import * as React from 'react';
import moment from 'moment';
import styles from './PatientInsuranceCards.module.scss';
import { ActionButton, CommandBar, ContextualMenuItemType, Label, Panel, PanelType, Shimmer, ShimmerElementType, Stack } from '@fluentui/react';
import { deletePatientInsurance, fetchPatientInsurances, updatePatientInsurancesCoverageOrder } from '../../../api/patients';
import { PatientInsurancePlan } from '../../../api/patients.types';
import { OkCancelDialog } from '../../core/OkCancelDialog';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '../../../redux/message/actions';
import { ToastParams } from '../../../redux/message/types';
import { RootState } from '../../../redux/types';
import { CreatePatientInsurancePlanForm } from './CreateInsurancePlanForm';
import { css } from '../../../util/Css';

const INSURANCE_ORDER = ['PRIMARY', 'SECONDARY', 'TERTIARY'];

interface Props {
  patientId: string;
}

export const PatientInsuranceCards = ({ patientId }: Props) => {
  const [insurances, setInsurances] = useState<PatientInsurancePlan[]>([]);
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const dispatchToast = useCallback((params: ToastParams) => dispatch(toast(params)), [dispatch]);

  const payers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers ?? []);

  const getInsurances = useCallback(() => {
    setLoading(true);
    fetchPatientInsurances(patientId)
      .then(setInsurances)
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => {
    getInsurances();
  }, [getInsurances]);

  const onClickRemoveInsurancePlan = useCallback(
    (planId: string, planName: string) => {
      OkCancelDialog.show({
        title: 'Remove Insurance Plan',
        subText: `Are you sure you want to remove ${planName}?`,
        okText: 'Yes, remove plan',
        onOk: () =>
          deletePatientInsurance(patientId, planId)
            .then(() => {
              dispatchToast({ type: 'success', title: 'Success', content: 'Patient insurance plan was successfully removed.' });
              getInsurances();
            })
            .catch((err: Error) => dispatchToast({ type: 'error', title: 'Error', content: err.message })),
      });
    },
    [dispatchToast, getInsurances, patientId]
  );

  function onClickUpdateInsuranceOrder(fromIndex: number, toIndex: number) {
    const insuranceIds = insurances.map((plan) => plan.id);
    const [removed] = insuranceIds.splice(fromIndex, 1);
    insuranceIds.splice(toIndex, 0, removed);

    setLoading(true);
    updatePatientInsurancesCoverageOrder(patientId, insuranceIds).then(() => {
      dispatchToast({ type: 'success', title: 'Success', content: 'Update was successful.' });
      getInsurances();
    });
  }

  if (loading) {
    return (
      <Stack horizontal tokens={{ childrenGap: 12 }}>
        {Array.from({ length: Math.max(insurances.length, 1) }).map((_, index) => (
          <Shimmer
            key={index}
            className={styles.cardContainer}
            width={400}
            shimmerColors={{ shimmer: '#f7fafc' }}
            shimmerElements={[{ type: ShimmerElementType.line, height: 240 }]}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack horizontal tokens={{ childrenGap: 12 }}>
      {insurances.map((insurance, index) => {
        return (
          <Stack className={css(styles.cardContainer, 'ms-depth-8')}>
            <Stack horizontal verticalAlign="center" horizontalAlign="space-between" className={styles.cardHeader}>
              <Label className="ms-font-l">{INSURANCE_ORDER.at(index) || 'n/a'}</Label>
              <CommandBar
                items={[]}
                overflowItems={[
                  {
                    key: 'primary',
                    disabled: index === 0,
                    text: 'Make primary',
                    onClick: () => onClickUpdateInsuranceOrder(index, 0),
                    iconProps: { iconName: 'PublicContactCard' },
                  },
                  {
                    key: 'secondary',
                    disabled: index === 1 || insurances.length < 2,
                    text: 'Make secondary',
                    onClick: () => onClickUpdateInsuranceOrder(index, 1),
                    iconProps: { iconName: 'PublicContactCard' },
                  },
                  {
                    key: 'teritiary',
                    disabled: index === 2 || insurances.length < 3,
                    text: 'Make teritiary',
                    iconProps: { iconName: 'PublicContactCard' },
                    onClick: () => onClickUpdateInsuranceOrder(index, 2),
                  },
                  { key: 'header', itemType: ContextualMenuItemType.Divider },
                  { key: 'delete', text: 'Delete', onClick: () => onClickRemoveInsurancePlan(insurance.id, insurance.planName), iconProps: { iconName: 'Delete' } },
                ]}
              />
            </Stack>

            <Stack horizontal horizontalAlign="space-between" key={index} style={{ padding: 12 }}>
              <Stack>
                <Field label="Payer" value={insurance.payer.name} />
                <Field label="Plan" value={insurance.planName} />
                <Field label="Member ID" value={insurance.memberId} />
                <Field label="Group ID" value={insurance.groupId || 'n/a'} />
              </Stack>
              <Stack>
                <Field label="Status" value={insurance.status || 'n/a'} />
                <Field
                  label="Plan Dates"
                  value={`${insurance.planStart ? moment(insurance.planStart).format('MM/DD/YY') : 'n/a'} - ${
                    insurance.planEnd ? moment(insurance.planEnd).format('MM/DD/YY') : 'n/a'
                  }`}
                />
                <Stack verticalAlign="end" verticalFill>
                  <Field label="Last Checked" value={insurance.statusUpdateDate ? moment(insurance.statusUpdateDate).tz('America/Los_Angeles').format('MM/DD/YY h:mm A') : 'n/a'} />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        );
      })}
      {insurances.length < 3 && (
        <Stack horizontalAlign="center" verticalAlign="center" className={styles.addContainer}>
          <ActionButton iconProps={{ iconName: 'Add' }} onClick={() => setShowAddInsurance(true)}>
            Add Insurance
          </ActionButton>
        </Stack>
      )}

      <Panel isOpen={showAddInsurance} onDismiss={() => setShowAddInsurance(false)} type={PanelType.smallFixedFar} headerText="Add Insurance Plan">
        <CreatePatientInsurancePlanForm
          payers={payers}
          patientId={patientId}
          onSuccess={() => {
            setShowAddInsurance(false);
            dispatchToast({ type: 'success', title: 'Success', content: 'Insurance plan was saved.' });
            getInsurances();
          }}
        />
      </Panel>
    </Stack>
  );
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack tokens={{ childrenGap: 2 }} style={{ margin: 4 }}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value}</div>
    </Stack>
  );
}
